import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import {
  MagnifyingGlassIcon,
  ClockIcon,
  ArrowLeftIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { searchService, type SearchResult } from '@/services/search.service';
import SearchResultItem from '@/components/search/SearchResultItem';
import { cn } from '@/utils/cn';

type ResultType = 'all' | 'incidents' | 'users' | 'threats' | 'investigations' | 'activities' | 'protectedPersons' | 'subjects';

export default function SearchResults() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // The global search bar in the header is the single source of the search term
  const query = searchParams.get('q') ?? '';

  const [selectedType, setSelectedType] = useState<ResultType>('all');
  const [recentSearches, setRecentSearches] = useState<string[]>([]);

  // Load recent searches from localStorage
  useEffect(() => {
    const stored = localStorage.getItem('recentSearches');
    if (stored) {
      try {
        setRecentSearches(JSON.parse(stored));
      } catch (e) {
        console.error('Failed to load recent searches:', e);
      }
    }
  }, []);

  // Reset the type filter when a new search arrives
  useEffect(() => {
    setSelectedType('all');
  }, [query]);

  // Fetch search results
  const { data, isLoading, isError } = useQuery({
    queryKey: ['search', query],
    queryFn: () => searchService.search({ query, limit: 10 }),
    enabled: query.length >= 2,
    staleTime: 30000,
    retry: 1,
  });

  // Filter results by type
  const getFilteredResults = (): SearchResult[] => {
    if (!data) { return []; }

    if (selectedType === 'all') {
      return [
        ...data.results.incidents,
        ...data.results.users,
        ...data.results.threats,
        ...data.results.investigations,
        ...data.results.activities,
        ...data.results.protectedPersons,
        ...data.results.subjects,
      ];
    }

    return data.results[selectedType] ?? [];
  };

  const allResults = getFilteredResults();
  const totalResults = selectedType === 'all' ? data?.counts.total ?? 0 : allResults.length;

  // Save search to recent searches
  const saveRecentSearch = useCallback((searchTerm: string) => {
    setRecentSearches(prev => {
      const updated = [searchTerm, ...prev.filter((s) => s !== searchTerm)].slice(0, 5);
      localStorage.setItem('recentSearches', JSON.stringify(updated));
      return updated;
    });
  }, []);

  // Handle result selection
  const handleSelectResult = useCallback((result: SearchResult) => {
    if (query) {
      saveRecentSearch(query);
    }

    // Navigate to appropriate page based on result type
    switch (result.type) {
      case 'incident':
        navigate(`/security/incidents/${result.id}`);
        break;
      case 'user':
        navigate(`/users/${result.id}`);
        break;
      case 'threat':
        navigate(`/security/threats/${result.id}`);
        break;
      case 'investigation':
        navigate(`/security/investigations/${result.id}`);
        break;
      case 'activity':
        navigate(`/security/executive-protection/activities/${result.id}`);
        break;
      case 'protectedPerson':
        navigate(`/security/executive-protection/${result.id}`);
        break;
      case 'subject':
        navigate(`/security/subjects/${result.id}`);
        break;
    }
  }, [query, navigate, saveRecentSearch]);

  // Handle recent search click — re-run the search via the URL
  const handleRecentSearch = (searchTerm: string) => {
    setSearchParams({ q: searchTerm });
  };

  // Clear recent searches
  const clearRecentSearches = () => {
    setRecentSearches([]);
    localStorage.removeItem('recentSearches');
  };

  const showResults = query.length >= 2;
  const showRecent = !showResults && recentSearches.length > 0;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.3 },
    },
  };

  const typeFilters: { value: ResultType; label: string; count?: number }[] = [
    { value: 'all', label: 'All Results', count: data?.counts.total },
    { value: 'incidents', label: 'Incidents', count: data?.counts.incidents },
    { value: 'users', label: 'Users', count: data?.counts.users },
    { value: 'activities', label: 'Activities', count: data?.counts.activities },
    { value: 'protectedPersons', label: 'Protected Persons', count: data?.counts.protectedPersons },
    { value: 'threats', label: 'Threats', count: data?.counts.threats },
    { value: 'investigations', label: 'Investigations', count: data?.counts.investigations },
    { value: 'subjects', label: 'Subjects', count: data?.counts.subjects },
  ];

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="min-h-screen bg-secondary-50 dark:bg-secondary-950 p-4 sm:p-6"
    >
      {/* Header */}
      <motion.div variants={itemVariants} className="max-w-6xl mx-auto mb-6">
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => navigate(-1)}
            aria-label="Go back"
          >
            <ArrowLeftIcon className="w-5 h-5" />
          </Button>
          <h1 className="text-2xl font-bold text-secondary-900 dark:text-white">
            {query ? (
              <>Search results for &quot;{query}&quot;</>
            ) : (
              'Search'
            )}
          </h1>
        </div>
      </motion.div>

      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar */}
          <motion.div variants={itemVariants} className="lg:col-span-1">
            {/* Type Filters */}
            {showResults && (
              <Card className="mb-4">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <FunnelIcon className="w-4 h-4 text-secondary-500" />
                    <h3 className="text-sm font-semibold text-secondary-700 dark:text-secondary-300">
                      Filter by Type
                    </h3>
                  </div>
                  <div className="space-y-1">
                    {typeFilters.map((filter) => (
                      <button
                        key={filter.value}
                        onClick={() => setSelectedType(filter.value)}
                        className={cn(
                          'w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors text-left',
                          selectedType === filter.value
                            ? 'bg-primary-100 text-primary-900 dark:bg-primary-900/20 dark:text-primary-100 font-medium'
                            : 'text-secondary-700 dark:text-secondary-300 hover:bg-secondary-100 dark:hover:bg-secondary-800'
                        )}
                      >
                        <span>{filter.label}</span>
                        {filter.count !== undefined && filter.count > 0 && (
                          <span className={cn(
                            'px-2 py-0.5 rounded-full text-xs',
                            selectedType === filter.value
                              ? 'bg-primary-200 dark:bg-primary-800'
                              : 'bg-secondary-200 dark:bg-secondary-700'
                          )}>
                            {filter.count}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Recent Searches */}
            {showRecent && (
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-secondary-700 dark:text-secondary-300">
                      Recent Searches
                    </h3>
                    <button
                      onClick={clearRecentSearches}
                      className="text-xs text-secondary-500 hover:text-secondary-700 dark:text-secondary-400 dark:hover:text-secondary-200"
                    >
                      Clear
                    </button>
                  </div>
                  <div className="space-y-1">
                    {recentSearches.map((searchTerm) => (
                      <button
                        key={searchTerm}
                        onClick={() => handleRecentSearch(searchTerm)}
                        className="w-full flex items-center px-3 py-2 hover:bg-secondary-100 dark:hover:bg-secondary-800 rounded-lg transition-colors text-left group"
                      >
                        <ClockIcon className="w-4 h-4 text-secondary-400 dark:text-secondary-500 mr-2 flex-shrink-0" />
                        <span className="flex-1 text-sm text-secondary-700 dark:text-secondary-300 truncate">
                          {searchTerm}
                        </span>
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </motion.div>

          {/* Results */}
          <motion.div variants={itemVariants} className="lg:col-span-3">
            {isLoading && (
              <Card>
                <CardContent className="p-12 text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4" />
                  <p className="text-secondary-600 dark:text-secondary-400">Searching...</p>
                </CardContent>
              </Card>
            )}

            {isError && (
              <Card>
                <CardContent className="p-12 text-center">
                  <p className="text-danger-600 dark:text-danger-400">
                    Failed to perform search. Please try again.
                  </p>
                </CardContent>
              </Card>
            )}

            {showResults && !isLoading && !isError && (
              <>
                {/* Results summary */}
                <div className="mb-4 text-sm text-secondary-600 dark:text-secondary-400">
                  {totalResults === 0 ? (
                    <span>No results found for &quot;{query}&quot;</span>
                  ) : (
                    <span>
                      Showing {totalResults} result{totalResults !== 1 ? 's' : ''} for &quot;{query}&quot;
                    </span>
                  )}
                </div>

                {totalResults === 0 ? (
                  <Card>
                    <CardContent className="p-12 text-center">
                      <MagnifyingGlassIcon className="w-16 h-16 text-secondary-300 dark:text-secondary-600 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-2">
                        No results found
                      </h3>
                      <p className="text-secondary-600 dark:text-secondary-400">
                        Try a different search term in the search bar above, or adjust the type filters
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-6">
                    {selectedType === 'all' ? (
                      <>
                        {/* Incidents */}
                        {data && data.results.incidents.length > 0 && (
                          <div>
                            <h2 className="text-lg font-semibold text-secondary-900 dark:text-white mb-3 flex items-center">
                              Incidents
                              <span className="ml-2 px-2 py-0.5 bg-secondary-100 dark:bg-secondary-800 text-secondary-600 dark:text-secondary-400 rounded-full text-sm">
                                {data.counts.incidents}
                              </span>
                            </h2>
                            <Card>
                              <CardContent className="p-2">
                                {data.results.incidents.map((result) => (
                                  <SearchResultItem
                                    key={result.id}
                                    result={result}
                                    onClick={() => handleSelectResult(result)}
                                  />
                                ))}
                              </CardContent>
                            </Card>
                          </div>
                        )}

                        {/* Users */}
                        {data && data.results.users.length > 0 && (
                          <div>
                            <h2 className="text-lg font-semibold text-secondary-900 dark:text-white mb-3 flex items-center">
                              Users
                              <span className="ml-2 px-2 py-0.5 bg-secondary-100 dark:bg-secondary-800 text-secondary-600 dark:text-secondary-400 rounded-full text-sm">
                                {data.counts.users}
                              </span>
                            </h2>
                            <Card>
                              <CardContent className="p-2">
                                {data.results.users.map((result) => (
                                  <SearchResultItem
                                    key={result.id}
                                    result={result}
                                    onClick={() => handleSelectResult(result)}
                                  />
                                ))}
                              </CardContent>
                            </Card>
                          </div>
                        )}

                        {/* Threats */}
                        {data && data.results.threats.length > 0 && (
                          <div>
                            <h2 className="text-lg font-semibold text-secondary-900 dark:text-white mb-3 flex items-center">
                              Threats
                              <span className="ml-2 px-2 py-0.5 bg-secondary-100 dark:bg-secondary-800 text-secondary-600 dark:text-secondary-400 rounded-full text-sm">
                                {data.counts.threats}
                              </span>
                            </h2>
                            <Card>
                              <CardContent className="p-2">
                                {data.results.threats.map((result) => (
                                  <SearchResultItem
                                    key={result.id}
                                    result={result}
                                    onClick={() => handleSelectResult(result)}
                                  />
                                ))}
                              </CardContent>
                            </Card>
                          </div>
                        )}

                        {/* Activities */}
                        {data && data.results.activities.length > 0 && (
                          <div>
                            <h2 className="text-lg font-semibold text-secondary-900 dark:text-white mb-3 flex items-center">
                              Activities
                              <span className="ml-2 px-2 py-0.5 bg-secondary-100 dark:bg-secondary-800 text-secondary-600 dark:text-secondary-400 rounded-full text-sm">
                                {data.counts.activities}
                              </span>
                            </h2>
                            <Card>
                              <CardContent className="p-2">
                                {data.results.activities.map((result) => (
                                  <SearchResultItem
                                    key={result.id}
                                    result={result}
                                    onClick={() => handleSelectResult(result)}
                                  />
                                ))}
                              </CardContent>
                            </Card>
                          </div>
                        )}

                        {/* Protected Persons */}
                        {data && data.results.protectedPersons.length > 0 && (
                          <div>
                            <h2 className="text-lg font-semibold text-secondary-900 dark:text-white mb-3 flex items-center">
                              Protected Persons
                              <span className="ml-2 px-2 py-0.5 bg-secondary-100 dark:bg-secondary-800 text-secondary-600 dark:text-secondary-400 rounded-full text-sm">
                                {data.counts.protectedPersons}
                              </span>
                            </h2>
                            <Card>
                              <CardContent className="p-2">
                                {data.results.protectedPersons.map((result) => (
                                  <SearchResultItem
                                    key={result.id}
                                    result={result}
                                    onClick={() => handleSelectResult(result)}
                                  />
                                ))}
                              </CardContent>
                            </Card>
                          </div>
                        )}

                        {/* Investigations */}
                        {data && data.results.investigations.length > 0 && (
                          <div>
                            <h2 className="text-lg font-semibold text-secondary-900 dark:text-white mb-3 flex items-center">
                              Investigations
                              <span className="ml-2 px-2 py-0.5 bg-secondary-100 dark:bg-secondary-800 text-secondary-600 dark:text-secondary-400 rounded-full text-sm">
                                {data.counts.investigations}
                              </span>
                            </h2>
                            <Card>
                              <CardContent className="p-2">
                                {data.results.investigations.map((result) => (
                                  <SearchResultItem
                                    key={result.id}
                                    result={result}
                                    onClick={() => handleSelectResult(result)}
                                  />
                                ))}
                              </CardContent>
                            </Card>
                          </div>
                        )}

                        {/* Subjects */}
                        {data && data.results.subjects.length > 0 && (
                          <div>
                            <h2 className="text-lg font-semibold text-secondary-900 dark:text-white mb-3 flex items-center">
                              Subjects
                              <span className="ml-2 px-2 py-0.5 bg-secondary-100 dark:bg-secondary-800 text-secondary-600 dark:text-secondary-400 rounded-full text-sm">
                                {data.counts.subjects}
                              </span>
                            </h2>
                            <Card>
                              <CardContent className="p-2">
                                {data.results.subjects.map((result) => (
                                  <SearchResultItem
                                    key={result.id}
                                    result={result}
                                    onClick={() => handleSelectResult(result)}
                                  />
                                ))}
                              </CardContent>
                            </Card>
                          </div>
                        )}
                      </>
                    ) : (
                      /* Filtered Results */
                      <Card>
                        <CardContent className="p-2">
                          {allResults.map((result) => (
                            <SearchResultItem
                              key={result.id}
                              result={result}
                              onClick={() => handleSelectResult(result)}
                            />
                          ))}
                        </CardContent>
                      </Card>
                    )}
                  </div>
                )}
              </>
            )}

            {/* Empty State - No search query */}
            {!showResults && !isLoading && (
              <Card>
                <CardContent className="p-12 text-center">
                  <MagnifyingGlassIcon className="w-16 h-16 text-secondary-300 dark:text-secondary-600 mx-auto mb-4 opacity-50" />
                  <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-2">
                    Start searching
                  </h3>
                  <p className="text-secondary-600 dark:text-secondary-400">
                    Use the search bar at the top of the page to search across incidents, activities, protected persons, threats, and investigations (minimum 2 characters)
                  </p>
                </CardContent>
              </Card>
            )}
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
}
